import React, { useState, useEffect, useRef } from "react";
import { parseISO, format } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";
import { 
  makeStyles, 
  Paper, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  TextField, 
  Typography, 
  CircularProgress, 
  IconButton, 
  Switch, 
  Avatar, 
  Chip, 
  Divider, 
  Grid, 
  InputAdornment, 
  Tooltip,
  Box,
  Fade,
  Button
} from "@material-ui/core";

import {
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  DeleteOutline as DeleteOutlineIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Android as BotIcon,
  WhatsApp as WhatsAppIcon,
  Info as InfoIcon,
  Palette as PaletteIcon,
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
import { green, red, blue, indigo } from "@material-ui/core/colors";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: "16px",
    background: theme.palette.background.paper,
    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
  },
  dialogTitle: {
    backgroundColor: "#6517ab",
    color: "white",
    padding: "16px 24px",
  },
  dialogContent: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
  },
  dialogActions: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    justifyContent: "space-between",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  formControl: {
    margin: theme.spacing(1, 0),
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
      borderRadius: "8px",
    },
  },
  sectionTitle: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  infoBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
  },
  avatar: {
    backgroundColor: "rgba(255,255,255,0.2)",
    marginRight: theme.spacing(2),
  },
  chip: {
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "white",
    fontWeight: "bold",
  },
  deleteButton: {
    backgroundColor: "#FF6B6B",
    color: "white",
    borderRadius: "10px",
    padding: "8px",
    "&:hover": {
      backgroundColor: "#e55a5a",
    },
  },
  saveButton: {
    backgroundColor: "#6517ab",
    color: "white",
    "&:hover": {
      backgroundColor: "#52128a",
    },
  },
  cancelButton: {
    backgroundColor: "#db6565",
    color: "white",
    "&:hover": {
      backgroundColor: "#c55a5a",
    },
  }
}));

const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Curto!")
    .max(250, "Longo!")
    .required("Obrigatótio"),
  number: Yup.string().min(8, "Curto!").max(50, "Longo!"),
  email: Yup.string().email("Email Inválido"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    number: "",
    email: "",
    disableBot: false,
    lgpdAcceptedAt: "",
  };

  const [contact, setContact] = useState(initialState);
  const [disableBot, setDisableBot] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        setContact((prevState) => ({
          ...prevState,
          ...initialValues,
        }));
      }

      if (!contactId) return;

      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        if (isMounted.current) {
          setContact(data);
          setDisableBot(data.disableBot);
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchContact();
  }, [contactId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setContact(initialState);
  };

  const handleSaveContact = async (values) => {
    try {
      if (contactId) {
        await api.put(`/contacts/${contactId}`, { ...values, disableBot });
        handleClose();
      } else {
        const { data } = await api.post("/contacts", { ...values, disableBot });
        if (onSave) onSave(data);
        handleClose();
      }
      toast.success(i18n.t("contactModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
      TransitionComponent={Fade}
    >
      <DialogTitle id="draggable-dialog-title" className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Avatar className={classes.avatar}>
              <PersonIcon />
            </Avatar>
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              {contactId
                ? i18n.t("contactModal.title.edit")
                : i18n.t("contactModal.title.add")}
            </Typography>
          </Box>
          <Chip
            label={contactId ? "Edição" : "Criação"}
            size="small"
            className={classes.chip}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Formik
          initialValues={contact}
          enableReinitialize
          validationSchema={ContactSchema}
          onSubmit={(values, actions) => {
            handleSaveContact(values);
            actions.setSubmitting(false);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <Box>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  <PaletteIcon style={{ marginRight: 8, fontSize: "1.2rem" }} />
                  {i18n.t("contactModal.form.mainInfo")}
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("contactModal.form.name")}
                      name="name"
                      fullWidth
                      autoFocus
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("contactModal.form.number")}
                      name="number"
                      fullWidth
                      error={touched.number && Boolean(errors.number)}
                      helperText={touched.number && errors.number}
                      variant="outlined"
                      className={classes.textField}
                      placeholder="5527996716613"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("contactModal.form.email")}
                      name="email"
                      fullWidth
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      variant="outlined"
                      className={classes.textField}
                      placeholder="email@example.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TagsContainer contact={contact} />
                  </Grid>
                </Grid>

                <Box className={classes.infoBox}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <BotIcon
                        style={{
                          color: disableBot ? red[500] : green[500],
                          marginRight: 8,
                        }}
                      />
                      <Typography variant="subtitle1">
                        {i18n.t("contactModal.form.chatBotContact")}
                      </Typography>
                      <Tooltip title="Habilitar/desabilitar interações do chatbot para este contato">
                        <InfoIcon
                          style={{ marginLeft: 8, color: "rgba(0,0,0,0.54)", fontSize: "1rem" }}
                        />
                      </Tooltip>
                    </Box>
                    <Switch
                      checked={!disableBot}
                      onChange={() => setDisableBot(!disableBot)}
                      color="primary"
                    />
                  </Box>
                </Box>

                <Box className={classes.infoBox}>
                  <Typography
                    variant="subtitle1"
                    style={{ marginBottom: 8, display: "flex", alignItems: "center" }}
                  >
                    <WhatsAppIcon
                      style={{
                        color: green[500],
                        marginRight: 8,
                      }}
                    />
                    {i18n.t("contactModal.form.whatsapp")}
                    <Chip
                      label={
                        contact?.whatsapp ? contact?.whatsapp.name : "Not linked"
                      }
                      size="small"
                      style={{ marginLeft: 8 }}
                      color={contact?.whatsapp ? "primary" : "default"}
                    />
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <InfoIcon
                      style={{
                        color: blue[500],
                        marginRight: 8,
                      }}
                    />
                    {i18n.t("contactModal.form.termsLGDP")}
                    <Chip
                      label={
                        contact?.lgpdAcceptedAt
                          ? format(
                              new Date(contact?.lgpdAcceptedAt),
                              "dd/MM/yyyy 'às' HH:mm"
                            )
                          : "Não Informado"
                      }
                      size="small"
                      style={{ marginLeft: 8 }}
                      color={contact?.lgpdAcceptedAt ? "primary" : "default"}
                    />
                  </Typography>
                </Box>

                <FieldArray name="extraInfo">
                  {({ push, remove }) => (
                    <>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        <AddIcon style={{ marginRight: 8, fontSize: "1.2rem" }} />
                        {i18n.t("contactModal.form.extraInfo")}
                      </Typography>

                      {values.extraInfo &&
                        values.extraInfo.map((info, index) => (
                          <Grid container spacing={2} key={index} alignItems="center" style={{ marginBottom: 16 }}>
                            <Grid item xs={5}>
                              <Field
                                as={TextField}
                                label={i18n.t("contactModal.form.extraName")}
                                name={`extraInfo[${index}].name`}
                                fullWidth
                                variant="outlined"
                                className={classes.textField}
                              />
                            </Grid>
                            <Grid item xs={5}>
                              <Field
                                as={TextField}
                                label={i18n.t("contactModal.form.extraValue")}
                                name={`extraInfo[${index}].value`}
                                fullWidth
                                variant="outlined"
                                className={classes.textField}
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <IconButton
                                onClick={() => remove(index)}
                                className={classes.deleteButton}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        ))}

                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => push({ name: "", value: "" })}
                        variant="contained"
                        className={classes.saveButton}
                        style={{ marginTop: 8 }}
                      >
                        {i18n.t("contactModal.buttons.addExtraInfo")}
                      </Button>
                    </>
                  )}
                </FieldArray>
              </Box>

              <Divider style={{ margin: "16px 0" }} />

              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={handleClose}
                  startIcon={<CancelIcon />}
                  variant="contained"
                  className={classes.cancelButton}
                >
                  {i18n.t("contactModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.saveButton}
                >
                  {contactId
                    ? i18n.t("contactModal.buttons.okEdit")
                    : i18n.t("contactModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      style={{ color: "white", marginLeft: 8 }}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;